/**
 * Voters workspace — every user-facing sentence, in one place.
 *
 * The vocabulary is a contract: the button says the verb, the confirmation says the same verb in the
 * past tense ("Add voter" → "Voter added", "Delete voter" → "Voter deleted"). Errors say what to do
 * next, never only what went wrong.
 */

const n = (count: number) => count.toLocaleString();

export const VOTERS_COPY = {
	page: {
		title: 'Voters',
		subtitle: 'The voter register: add people one at a time, import a file, and see what every import did.',
		importFile: 'Import file',
		addVoter: 'Add voter',
		tabs: {
			register: 'Register',
			import: 'Import',
			importing: (percent: number) => `Import · ${percent}%`,
			history: 'Import history',
		},
		tabsLabel: 'Voters sections',
	},
	register: {
		searchPlaceholder: 'Search name, phone or VIN',
		searchLabel: 'Search the register',
		empty: 'No voters yet — add one or import a file.',
		emptyFiltered: 'No voters match these filters.',
		clearFilters: 'Clear filters',
		error: "The register didn't load.",
		retry: 'Try again',
		rateLimited: 'Too many searches in a row — wait a moment, then try again.',
		unavailable: 'The register list needs a newer server. Imports and Add voter still work.',
		superAdminOnly: 'The register is visible to Super Admins only.',
		columns: {
			name: 'Name',
			phone: 'Phone',
			pu: 'Polling unit',
			source: 'Source',
			addedBy: 'Added by',
			addedOn: 'Added on',
		},
		allStates: 'All states',
		allLgas: 'All LGAs',
		allWards: 'All wards',
		count: (total: number) => `${n(total)} ${total === 1 ? 'voter' : 'voters'}`,
	},
	source: (source: string, importNumber: number | null) =>
		source === 'Import'
			? importNumber
				? `Import #${importNumber}`
				: 'Import'
			: source === 'Manual'
				? 'Manual'
				: 'Earlier upload',
	history: {
		empty: 'Files you import will be listed here with what was added and what needs fixing.',
		error: "Import history didn't load.",
		unavailable: 'Import history needs a newer server. Imports still work — their results show on the Import tab.',
		columns: {
			number: 'Import',
			file: 'File',
			by: 'By',
			date: 'Started',
			added: 'Added',
			// The summary counts every row that did not import in one number; the split into "already
			// registered" and "to fix" is per row, in the rows-to-fix file.
			notAdded: 'Not added',
			status: 'Status',
		},
	},
	batch: {
		title: (number: number) => `Import #${number}`,
		downloadFailures: 'Download rows to fix',
		downloading: 'Preparing…',
		noFailures: 'Every row in this import was added — nothing to fix.',
		notAddedHint: 'The rows-to-fix file lists each one with why: already registered, or what to change.',
		resumeHint: (next: number, total: number) =>
			`Stopped after chunk ${n(next)} of ${n(total)}. To continue, open the Import tab and choose the same file again — it picks up where it stopped.`,
		inProgressHint: 'Rows are still arriving from the browser that started this import.',
		failedDownload: "The rows-to-fix file didn't download. Try again.",
	},
	import: {
		finished: (added: number, toFix: number) => `Import finished: ${n(added)} added, ${n(toFix)} to fix`,
		stopped: (added: number, toFix: number) => `Import stopped: ${n(added)} added, ${n(toFix)} to fix so far`,
		added: 'Added',
		already: 'Already registered',
		toFix: 'To fix',
		addedHint: 'New voters now in the register.',
		alreadyHint: 'Their phone or VIN was registered before — nothing to do.',
		toFixHint: 'Fix these in the downloaded file and import it again.',
		downloadRowsToFix: 'Download rows to fix',
		startNew: 'Import another file',
		backToReview: 'Back to review',
		openHistory: 'Open in Import history',
		resumeTitle: (number: number) => `You started importing this file as Import #${number}.`,
		resumeBody: (rows: number) =>
			`Continue it and only the rows the server hasn't confirmed are sent — ${n(rows)} rows in total. Keep the same columns.`,
		resumeConfirm: 'Continue that import',
		resumeDismiss: 'Start a new import',
		resuming: (number: number) => `Continuing Import #${number}.`,
		sameFile: (numbers: readonly number[]) =>
			`This file was imported before (${numbers.map((x) => `Import #${x}`).join(', ')}). Rows already in the register will be reported as already registered.`,
		reconciling: 'All rows sent — reading back which ones need fixing…',
		reconcileFailed: (number: number) =>
			`All rows were sent, but the list of rows that need fixing didn't load. Download it from Import #${number} in Import history.`,
		waiting: {
			'rate-limited': 'The server asked us to slow down — continuing in a moment.',
			'retrying': 'The connection dropped — retrying this chunk.',
			'signed-out': 'Your session ended. Sign in again in another tab, then press Resume — nothing has been lost.',
		},
	},
	form: {
		placeSection: 'Polling unit',
		personSection: 'Voter',
		contactSection: 'Contact',
		idSection: 'Identity',
		required: 'Required',
		optional: 'Optional',
		firstName: 'First name',
		middleName: 'Middle name',
		lastName: 'Last name',
		phone: 'Phone number',
		email: 'Email',
		vin: 'VIN',
		gender: 'Gender',
		genderUnset: 'Not recorded',
		dob: 'Date of birth',
		dobHint: 'Must be 18 or older.',
		phoneEcho: (local: string) => `Saves as ${local}`,
		phoneKeep: 'Leave blank to keep the current number',
		addVoter: 'Add voter',
		saveAndAnother: 'Save and add another',
		saveChanges: 'Save changes',
		saving: 'Saving…',
		close: 'Close',
		added: 'Voter added',
		changesSaved: 'Changes saved',
		lastAdded: 'Last added:',
		keyHint: 'Enter adds · ⌘/Ctrl+Enter adds and keeps the polling unit for the next voter',
		discardTitle: 'Discard this voter?',
		discardBody: "What you've typed for this voter hasn't been saved.",
		discardConfirm: 'Discard',
		discardCancel: 'Keep editing',
		checking: 'Checking the register…',
		legacyNote: 'This server still needs an email and a VIN for every voter.',
	},
	duplicate: {
		phoneTitle: 'Another voter already uses this number.',
		vinTitle: 'This VIN is already registered — open their record instead.',
		// Under the field while the card below explains the match: the field says what to do, the
		// card says what was found, so neither repeats the other.
		phoneFieldHint: 'Change the number, or choose Save anyway below.',
		vinFieldHint: 'Remove or correct the VIN — it matches the voter below.',
		more: (count: number) => `and ${n(count)} more`,
		openRecord: 'Open record',
		saveAnyway: 'Save anyway',
		cancelOverride: 'Cancel',
		reasonLabel: 'Why is this a different person?',
		reasonHint: 'Required, 10–500 characters. Saved with the record and shown to Super Admins.',
		reasonTooShort: 'Say a little more — at least 10 characters.',
		reasonTooLong: 'Keep the reason under 500 characters.',
		overrideOn: 'Saving anyway — this voter will be linked to the existing record.',
		addedOn: (date: string) => `added ${date}`,
	},
	place: {
		stateLabel: 'State',
		lgaLabel: 'LGA',
		wardLabel: 'Ward',
		puLabel: 'Polling unit',
		statePlaceholder: 'Choose a state',
		lgaPlaceholder: 'Choose an LGA',
		wardPlaceholder: 'Choose a ward',
		puPlaceholder: 'Search polling units',
		lgaWait: 'Choose a state first.',
		wardWait: 'Choose an LGA first.',
		puWait: 'Choose a ward first.',
		loadError: "This list didn't load.",
		retry: 'Retry',
		empty: (parent: string) => `No entries under ${parent}.`,
		codeLabel: 'Polling unit code',
		pasteLabel: 'Paste a polling-unit code',
		pastePlaceholder: 'e.g. 04-09-02-011',
		pasteApply: 'Fill',
		widenTo: (segment: string) => `Go back to ${segment}`,
		srResolved: (caption: string, code: string) => `Polling unit set: ${caption}, ${code}`,
	},
	detail: {
		title: 'Voter',
		reveal: 'Reveal',
		revealing: 'Revealing…',
		hide: 'Hide',
		revealNote: 'Revealing is logged against your account.',
		phone: 'Phone',
		email: 'Email',
		vin: 'VIN',
		gender: 'Gender',
		dob: 'Date of birth',
		pu: 'Polling unit',
		source: 'Source',
		addedBy: 'Added by',
		addedOn: 'Added on',
		override: 'Saved over a duplicate',
		notRecorded: 'Not recorded',
		edit: 'Edit voter',
		delete: 'Delete voter',
		deleteTitle: 'Delete this voter?',
		deleteBody: (name: string) =>
			`${name} will be removed from the register and from broadcast audiences. The record is kept for audit, with your reason.`,
		deleteReasonLabel: 'Reason for deleting',
		deleteReasonHint: 'Required, 10–500 characters. Saved with the deletion.',
		deleteReasonTooShort: 'Say a little more — at least 10 characters.',
		deleteConfirm: 'Delete voter',
		deleteCancel: 'Cancel',
		deleted: 'Voter deleted',
		error: "This voter didn't load.",
		unavailable: 'Voter records need a newer server.',
	},
} as const;
